import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { CHARGE_SCOPES, type ChargeScope } from './charges/chargeScope'
import type { ChargeWindow } from './useBusinessQueries'

export const PERIODS = [30, 90, 365] as const
export type ChargeView = 'amount' | 'share'

export interface BusinessFilters {
  days: number
  window: ChargeWindow
  view: ChargeView
  scope: ChargeScope
}

/** Unknown or missing values fall back to the defaults, so a pasted link never breaks the page. */
export function parseBusinessFilters(params: URLSearchParams): BusinessFilters {
  const days = Number(params.get('days'))
  return {
    days: (PERIODS as readonly number[]).includes(days) ? days : PERIODS[0],
    window: params.get('window') === 'MONTH' ? 'MONTH' : 'DAY',
    view: params.get('view') === 'share' ? 'share' : 'amount',
    scope:
      CHARGE_SCOPES.find((scope) => scope === params.get('scope')) ??
      'customers',
  }
}

/** Filters live in the URL so a view can be shared and survives a reload. */
export function useBusinessFilters() {
  const [params, setParams] = useSearchParams()
  const filters = parseBusinessFilters(params)
  const update = useCallback(
    (patch: Partial<BusinessFilters>) => {
      // The address bar, not a render-time copy, so quick successive changes all survive.
      const next = new URLSearchParams(window.location.search)
      for (const [key, value] of Object.entries(patch)) {
        next.set(key, String(value))
      }
      setParams(next, { replace: true })
    },
    [setParams],
  )
  return [filters, update] as const
}
